import React from 'react';
import { useStore } from 'react-redux';
import {
  useConfig,
  useGetPath,
  usePush,
  useAnalyticsTracking,
  useSetDocumentMeta,
  useFeatureFlag,
  useSetInfoMessage,
  useSetErrorMessage,
  useSetWarningMessage,
  useSetSuccessMessage
} from '@mulesoft/exchange-react-hooks';
import { loadReducers } from '@mulesoft/exchange-ui-portals-store/lib/store/reducerManager/actions';
import { useFederatedModule } from '../../../components/hooks';

const processPlugins = (portalConfiguration) => {
  if (!portalConfiguration?.default?.plugins) {
    return portalConfiguration;
  }

  return {
    default: {
      ...portalConfiguration.default,
      reducers: portalConfiguration.default.plugins.reduce((accum, current) => {
        if (current.reducers) {
          const pluginReducers = current.reducers();

          return { ...accum, ...pluginReducers };
        }

        return accum;
      }, portalConfiguration.default.reducers),
      onPrepare: (props) => {
        portalConfiguration.default.onPrepare?.(props);

        portalConfiguration.default.plugins.forEach((current) => {
          if (current.onPrepare) {
            current.onPrepare(props);
          }
        });
      },
      onLoad: (props) => (dispatch) => {
        if (portalConfiguration.default.onLoad) {
          dispatch(portalConfiguration.default.onLoad(props));
        }

        portalConfiguration.default.plugins.forEach((current) => {
          if (current.onLoad) {
            dispatch(current.onLoad(props));
          }
        });
      }
    }
  };
};

export const usePortalsConfiguration = ({
  asset = {},
  route,
  disableEvents
}) => {
  const config = useConfig();
  const typeVersion = useFeatureFlag(`${asset.type}-version`);
  const [isPrepared, setIsPrepared] = React.useState(false);
  const store = useStore();
  const getPath = useGetPath();
  const push = usePush();
  const trackEvent = useAnalyticsTracking();
  const setInfoMessage = useSetInfoMessage();
  const setErrorMessage = useSetErrorMessage();
  const setWarningMessage = useSetWarningMessage();
  const setSuccessMessage = useSetSuccessMessage();
  const setDocumentMeta = useSetDocumentMeta();
  const helpers = React.useMemo(
    () => ({
      getPath,
      push,
      trackEvent,
      setDocumentMeta,
      setInfoMessage,
      setErrorMessage,
      setWarningMessage,
      setSuccessMessage
    }),
    [
      getPath,
      push,
      trackEvent,
      setDocumentMeta,
      setInfoMessage,
      setErrorMessage,
      setWarningMessage,
      setSuccessMessage
    ]
  );
  const { federatedContent, failed } = useFederatedModule(
    'assets_definitions',
    `./${asset.type}-portal`,
    typeVersion
  );
  const portalConfiguration = React.useMemo(() => {
    if (federatedContent) {
      return processPlugins(federatedContent);
    }

    return null;
  }, [federatedContent]);

  React.useEffect(() => {
    async function prepare() {
      const { onPrepare } = portalConfiguration.default;

      if (!onPrepare) {
        setIsPrepared(true);

        return;
      }

      try {
        await onPrepare({
          asset,
          isDraft: route.isDraft,
          isEdit: route.isEdit,
          helpers,
          config
        });

        setIsPrepared(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    if (!portalConfiguration?.default || isPrepared || disableEvents) {
      return;
    }

    if (asset.isCompleted) {
      prepare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    asset.groupId,
    asset.assetId,
    asset.isCompleted,
    portalConfiguration,
    disableEvents
  ]);

  React.useEffect(() => {
    if (disableEvents) {
      return;
    }

    if (asset.isCompleted && portalConfiguration && isPrepared) {
      if (!portalConfiguration?.default) {
        // leaving this to troubleshoot a weird bug with federated modules
        // eslint-disable-next-line no-console
        console.error(
          'Failed to retrieve portalConfiguration',
          portalConfiguration
        );

        return;
      }

      const onLoad = store.dispatch(
        loadReducers({
          asset,
          reducerManager: store.reducerManager,
          portalConfiguration
        })
      );

      if (onLoad) {
        store.dispatch(
          onLoad({
            asset,
            isDraft: route.isDraft,
            isEdit: route.isEdit,
            helpers,
            config
          })
        );
      }
    }
  }, [
    asset,
    portalConfiguration,
    config,
    helpers,
    store,
    route,
    isPrepared,
    disableEvents
  ]);

  return {
    portalConfiguration,
    failed
  };
};
